import { loadFonts } from 'hooks/useFont';
import { RootState, store } from 'modules/Editor/store';
import {
  selectAllPageBackgroundData,
  selectPageCount,
  selectPageNumber,
  selectPageNumberCustomRange,
  selectPageNumberShowOnPages,
  selectPageNumberStart,
  selectPageOrder,
} from 'modules/Editor/store/infographSelector';
import { findFontsInPage } from 'modules/Editor/store/infographSlice.helpers';
import { moveToPage, setFontsLoaded } from 'modules/Editor/store/pageControlSlice';
import { PageId } from 'types/idTypes';
import { Widget, WidgetType } from 'types/widget.types';
import { getPageWidgetIds, getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { AllWidgetData } from 'widgets/Widget.types';

/**
 * Move to the page based on pageId parameter
 * and load the fonts used in the page.
 * @param pageId
 */
export const movePage = (pageId: PageId) => {
  const { infograph } = store.getState();
  const { pageOrder } = infograph;
  store.dispatch(setFontsLoaded(false));
  store.dispatch(moveToPage({ pageOrder, pageId }));
  loadFonts(findFontsInPage(infograph, pageId)).finally(() => {
    store.dispatch(setFontsLoaded(true));
  });
};

/**
 * Get all the text widgets based on the pageId
 * @param pageId
 */
export const getPageTextWidgetsData = (pageId: PageId) => {
  const { infograph } = store.getState();
  const page = infograph.pages[pageId];
  const pageWidgetIds = getPageWidgetIds(page, infograph.widgets, false);
  const pageTextWidgetIds = pageWidgetIds.filter((widgetId) => getWidgetTypeFromId(widgetId) === WidgetType.Text);
  const pageTextWidgetsData = pageTextWidgetIds.reduce((widgetsData, widgetId) => {
    widgetsData[widgetId] = infograph.widgets[widgetId];
    return widgetsData;
  }, {} as { [key: string]: Widget | AllWidgetData });
  return pageTextWidgetsData;
};

/**
 * Get the pagenumbers and the pages with pagenumbers
 * If there is
 * @param pageRange  PageId[]
 *
 * @returns {pageNumber, pagesWithPageNumber}
 * Example:
 * {
 *  // The infograph page number setting
 *  pageNumber: { isEnabled: true, showOnPages: 'all', start: 1, range: [], },
 *  // The pages that have page numbers
 *  pagesWithPageNumber: '1,2,3,4,5'
 * }
 */
export const getPageNumber = (pageRange: PageId[]) => {
  const state = store.getState() as RootState;
  const pageNumber = selectPageNumber(state);

  if (!pageNumber || !pageNumber?.isEnabled) return { pageNumber: undefined, pagesWithPageNumber: '' };

  const showOnPages = selectPageNumberShowOnPages(state);
  const customRange = selectPageNumberCustomRange(state);
  const pageCount = selectPageCount(state);
  const pageNumberStart = selectPageNumberStart(state);
  const pageOrder = selectPageOrder(state);

  let pageNumberArray: number[] = [];
  // Get the page numbers after the pageNumberStart
  if (showOnPages === 'all') {
    for (let i = pageNumberStart; i <= pageCount; i++) {
      const pageId = pageOrder[i - 1];

      if (pageRange.includes(pageId)) {
        pageNumberArray.push(i);
      }
    }
  }

  if (showOnPages === 'custom') {
    for (let i = 0; i < customRange.length; i++) {
      const pageId = pageOrder[customRange[i] - 1];

      if (pageRange.includes(pageId)) {
        pageNumberArray.push(customRange[i]);
      }
    }
  }

  return { pageNumber, pagesWithPageNumber: pageNumberArray.join(',') };
};

/**
 * Get the page background from a specific pageId
 * Used when exporting the infograph
 *
 * @param pageId
 */
export const getPageBackground = (pageId: PageId) => {
  if (!pageId) return;

  const state = store.getState() as RootState;
  const pageBackground = selectAllPageBackgroundData(pageId)(state);
  const { background, gradientBackground } = pageBackground;
  const pageColor = gradientBackground ? gradientBackground?.colors?.[0] || '' : background;
  return pageColor;
};
